/**
 * The menus (spec 08 §1, §3-5): the title's menu, options, controls, credits, the change list and the
 * pause menu, drawn in the game's font on the HUD canvas and driven one 50 Hz frame at a time.
 *
 * CONTRACT (integrator-owned signatures; the MENUS stream fills in the bodies and adds files beside
 * this one). The session calls `update` once per frame while a screen is open, acts on the returned
 * action, and calls `draw` after the side panels each displayed frame.
 *
 * The state machine is `MenuModel` (model.ts, no canvas); `Menus` adds the painter (paint.ts).
 * A `start`, `resume` or `quit` action closes the menus: after a quit, open the title again.
 */

import type { HudColours } from '../hud/panels';
import { MenuModel } from './model';
import { type MenuColours, paint } from './paint';

export type { MenuColours } from './paint';
export type { MenuRow } from './screens';
export { RESERVED_KEYS } from './rebind';

export type MenuScreen = 'title' | 'options' | 'controls' | 'credits' | 'changes' | 'pause';

/** One frame of menu controls, already merged from the keyboard, the pads and the mouse. */
export interface MenuInput {
  /** Edges: true on the frame the direction or button went down (the integrator adds key repeat). */
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  /** Enter, player 1's fire, pad A. */
  select: boolean;
  /** Esc, Backspace, pad B. */
  back: boolean;
  /** Player 2's fire (on the title it starts a two-player game at once, as on the C64). */
  p2Fire: boolean;
  /** The first KeyboardEvent.code tapped this frame, for rebinding; null if none. */
  key: string | null;
  /** The mouse in HUD pixels (640x360), and whether it clicked this frame; null when it has not moved. */
  pointer: { x: number; y: number; click: boolean } | null;
}

export type MenuAction =
  | { type: 'start'; players: 1 | 2 }
  | { type: 'resume' }
  | { type: 'quit' };

export class Menus extends MenuModel {
  /**
   * Draw the open screen over the HUD (after the panels). Plain `HudColours` work; a `MenuColours`
   * adds the panel, shadow and warning colours (otherwise taken from the core palette's ink and accent).
   */
  draw(ctx: CanvasRenderingContext2D, colours: HudColours | MenuColours): void {
    paint(ctx, this, colours);
  }
}
